import React from 'react';
import { Box, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  EditableInput,
  NominalValueCell,
  ToleranceCell,
  CalculatedValueCell,
  PassFailStatusCell
} from '@features/calibration/components/shared';
import {
  formatValueWithResolution,
  getBodyCellSx
} from '@features/calibration/components/shared/utils';
import { TestType } from '@features/calibration/types';
import type { BasicReadingTestPoint } from '@features/calibration/hooks/useCalibrationRuntime/types';
import { calculateError, inferResolution } from '../utils';
import type { CommitMeasurementHandler, MeasurementSide } from '../types';
import MeasurementImageCell from './MeasurementImageCell';

type RunnerRowProps = {
  point: BasicReadingTestPoint;
  unit: string;
  showAsFound: boolean;
  showAsLeft: boolean;
  readOnly?: boolean;
  isCurrentPoint?: boolean;
  landedSide?: MeasurementSide | null;
  landedAt?: number | null;
  runId?: string;
  onCommitMeasurement?: CommitMeasurementHandler;
};

const RunnerRow: React.FC<RunnerRowProps> = ({
  point,
  unit,
  showAsFound,
  showAsLeft,
  readOnly = false,
  isCurrentPoint = false,
  landedSide = null,
  landedAt = null,
  runId,
  onCommitMeasurement
}) => {
  const { t } = useTranslation('calibration');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const resolution = inferResolution(point);
  const readability = resolution > 0 ? Math.pow(10, -resolution) : 1;
  const pointUnit = point.unit ?? unit;

  const renderMeasuredCell = (side: MeasurementSide) => {
    const value = side === 'asFound' ? point.asFound : point.asLeft;
    const overridden =
      side === 'asFound' ? point.asFoundOverridden : point.asLeftOverridden;
    const section = side === 'asFound' ? 'asFound' : 'asLeft';

    const isReceivingSide =
      isCurrentPoint &&
      value === null &&
      (side === 'asFound' || point.asFound !== null);

    const justLanded = landedSide === side && landedAt !== null;

    if (isReceivingSide) {
      return (
        <TableCell sx={getBodyCellSx(section, isDarkMode)} align="center">
          <Box
            sx={{
              display: 'inline-block',
              border: '2px solid #f9a825',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              color: isDarkMode ? 'rgba(251, 191, 36, 1)' : '#9a6700'
            }}
          >
            <Typography variant="body2">{t('runner.reading')}</Typography>
          </Box>
        </TableCell>
      );
    }

    return (
      <TableCell
        key={justLanded ? `${side}-${landedAt}` : side}
        sx={{
          ...getBodyCellSx(section, isDarkMode),
          ...(justLanded && {
            '@keyframes runnerPulse': {
              from: { backgroundColor: 'rgba(249, 168, 37, 0.45)' },
              to: { backgroundColor: 'transparent' }
            },
            animation: 'runnerPulse 1.2s ease-out'
          })
        }}
        align="center"
      >
        {readOnly ? (
          <Typography variant="body2" align="right">
            {value !== null
              ? `${formatValueWithResolution(value, resolution)} ${pointUnit}`
              : '--'}
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'inline-block',
              width: '100%',
              ...(overridden && {
                border: '2px solid #f9a825',
                borderRadius: 2,
                backgroundColor: isDarkMode
                  ? 'rgba(249, 168, 37, 0.08)'
                  : '#fffdf5',
                p: 0.25
              })
            }}
          >
            <EditableInput
              field={side}
              value={value}
              point={{
                id: point.id,
                nominal: point.nominalValue,
                asFound: point.asFound,
                asLeft: point.asLeft
              }}
              readability={readability}
              resolution={resolution}
              unit={pointUnit}
              onUpdate={(field, updateValue) =>
                onCommitMeasurement?.(
                  point.id,
                  field as MeasurementSide,
                  updateValue === null || updateValue === ''
                    ? null
                    : Number(updateValue)
                )
              }
              testType={TestType.BasicReading}
              isDisabled={!onCommitMeasurement}
            />
          </Box>
        )}
        {overridden && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.25,
              color: isDarkMode ? 'rgba(251, 191, 36, 1)' : '#9a6700'
            }}
          >
            ✎ operator override
          </Typography>
        )}
      </TableCell>
    );
  };

  const renderSideCells = (side: MeasurementSide) => {
    const value = side === 'asFound' ? point.asFound : point.asLeft;
    const error = calculateError(value, point.nominalValue);
    const section = side === 'asFound' ? 'asFound' : 'asLeft';
    const imageId =
      side === 'asFound' ? point.asFoundImageId : point.asLeftImageId;

    return (
      <>
        {renderMeasuredCell(side)}
        <CalculatedValueCell
          value={error}
          tolerance={point.tolerance}
          resolution={resolution}
          section={section}
        />
        <PassFailStatusCell
          error={error}
          tolerance={point.tolerance}
          section={section}
        />
        <MeasurementImageCell
          imageId={imageId}
          runId={runId}
          section={section}
        />
      </>
    );
  };

  return (
    <TableRow
      hover
      sx={{
        ...(isCurrentPoint && {
          backgroundColor: isDarkMode ? 'rgba(249, 168, 37, 0.08)' : '#fffbea',
          '& td:first-of-type': { borderLeft: '3px solid #f9a825' }
        })
      }}
    >
      <NominalValueCell
        nominal={point.nominalValue}
        label={point.label}
        resolution={resolution}
        unit={pointUnit}
      />
      {showAsFound && renderSideCells('asFound')}
      {showAsLeft && renderSideCells('asLeft')}
      <ToleranceCell
        tolerance={point.tolerance}
        resolution={resolution}
        unit={pointUnit}
      />
    </TableRow>
  );
};

export default RunnerRow;
